import {
  Component,
  HostListener,
  OnInit,
  ViewChild,
  signal,
  effect,
  computed,
} from '@angular/core';
import { IconOptions, MatIconRegistry, MatIcon } from '@angular/material/icon';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { environment } from 'src/environments/environment';
import { saveOrgInfoLocal } from './globals';
import {
  DataStoreExport,
  Organisation,
  OrgUser,
  Particularity,
  PublicSettings,
  RootUser,
  ScreenSizeExport,
  Setting,
} from './interfaces';
import { Role } from './enums';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { Location } from '@angular/common';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatSidenavContainer, MatSidenav } from '@angular/material/sidenav';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { PermissionsService } from './services/permissions.service';
import { HelpDialogComponent } from './components/help-dialog/help-dialog.component';
import { buildMenuGroups, MenuGroup } from './navigation-helper';

export const app = initializeApp(environment.firebase);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'europe-west1');
export const auth = getAuth(app);
export const storage = getStorage(app);

const initialDataStore: DataStoreExport = {
  rootUser: null,
  orgUser: null,
  selectedOrganisation: null,
  isMemberOfMultipleOrgs: null,
  publicSettings: null,
  generalSettings: null,
  theming: null,
  paidFunctionalities: {},
  subscribes: [],
};
export const dataStoreSignal = signal<DataStoreExport>(initialDataStore);
export const dataStore: DataStoreExport = initialDataStore;
export function updateDataStore<K extends keyof DataStoreExport>(
  prop: K,
  value: DataStoreExport[K],
) {
  dataStore[prop] = value;
  dataStoreSignal.set({ ...dataStore });
}

export const screenSize: ScreenSizeExport = {
  height: null,
  width: null,
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatSidenavContainer,
    MatSidenav,
    RouterLink,
    RouterOutlet,
    MatProgressSpinner,
    MatTooltip,
    HelpDialogComponent,
  ],
})
export class AppComponent implements OnInit {
  showHelpPane = signal(false);
  menuExpanded = signal(false);
  @ViewChild('sidenav', { static: false }) sidenav: any;
  orgName = localStorage.getItem('orgName') ?? 'Buurtvervoer';
  profileButtons = computed(() => {
    const ds = dataStoreSignal();
    const orgUser = ds.orgUser;
    const rootUser = ds.rootUser;
    const roles: Role[] = orgUser?.roles ?? [];

    const isOtherOnly =
      rootUser?.rights !== 'admin' &&
      roles.length > 0 &&
      roles.every((role) => role === Role.Other);
    const canViewEmployees = ds.paidFunctionalities?.usesEmployeeListForStaff === true;

    const buttons: { name: string; icon: string; action: () => void }[] = [];

    if (!isOtherOnly || canViewEmployees) {
      buttons.push({
        name: 'Account',
        icon: 'account_circle',
        action: () => {
          this.router.navigate(['/account']);
        },
      });
    }

    buttons.push({
      name: 'Uitloggen',
      icon: 'exit_to_app',
      action: () => this.logout(),
    });

    return buttons;
  });

  showNavbar = signal(false);
  settingOrg = signal(false);
  loggingin = signal(false);
  loaded = signal(false);
  loadingPage = signal(true);
  dataStore: DataStoreExport = dataStore;
  RoleEnum = Role;

  orgWidth = signal('0px');
  profileWidth = signal('0px');
  menuGroups = signal<MenuGroup[]>([]);
  menuItemsCount = signal(0);

  constructor(
    public titleService: Title,
    private router: Router,
    private location: Location,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private permissionsService: PermissionsService,
  ) {
    effect(() => {
      const ds = dataStoreSignal();
      const orgUser = ds.orgUser;
      const rootUser = ds.rootUser;
      const roles = orgUser?.roles || [];
      const generalSettings = ds.generalSettings;

      // Determine visibility of Rooster using localStorage fallback to prevent disappearing on refresh
      const usesDriverPlanning =
        ds.paidFunctionalities?.usesDriverPlanning ??
        localStorage.getItem('usesDriverPlanning') === 'true';
      const groups = buildMenuGroups(
        {
          userId: orgUser?.id,
          roles,
          rootRights: rootUser?.rights,
          usesDriverPlanning,
          usesEmployeeListForStaff: ds.paidFunctionalities?.usesEmployeeListForStaff === true,
          ignoreDriverStatus: generalSettings?.ignoreDriverStatus,
          coDriverEnabled: generalSettings?.coDriverEnabled,
        },
        this.permissionsService,
      );

      this.menuGroups.set(groups);
      this.menuItemsCount.set(groups.reduce((acc, group) => acc + group.items.length, 0));
    });

    effect(() => {
      const theming = dataStoreSignal().theming;
      if (theming?.name) {
        this.titleService.setTitle(theming.name);
      }
      const faviconEl = document.querySelector('#favicon') as HTMLLinkElement | null;
      if (faviconEl && theming?.favicon) {
        faviconEl.href = theming.favicon;
      }
    });

    this.router.events.subscribe(async (event) => {
      if (
        !this.location.path().includes('/login') &&
        !this.isAuthAction() &&
        !this.location.path().includes('/selectingOrganisation') &&
        !this.location.path().includes('/set/')
      ) {
        this.showNavbar.set(true);
      } else {
        this.showNavbar.set(false);
      }
      if (
        this.location.path().includes('/set/') ||
        this.location.path().includes('/selectingOrganisation')
      ) {
        this.settingOrg.set(true);
      } else {
        this.settingOrg.set(false);
      }
      if (this.location.path().includes('/login') || this.isAuthAction()) {
        this.loggingin.set(true);
      } else {
        this.loggingin.set(false);
      }
      this.getScreenSize();
    });
  }

  @HostListener('window:resize', ['$event'])
  getScreenSize(event?) {
    screenSize.height = window.innerHeight;
    screenSize.width = window.innerWidth;
  }

  ngOnInit(): void {
    onAuthStateChanged(auth, async (user: User | null) => {
      if (this.isAuthAction()) {
        this.loaded.set(true);
        return;
      }
      if (!this.loggingin()) {
        if (user) {
          await initDataStoreRootUser(user, this.iconRegistry, this.sanitizer);
          await initDataStorePublicSettings();
          await initDataStoreGeneralSettings();
          await initDataStoreOrgUser();

          //TODO remove when dataStore is implemented everywhere in the system'
          if (dataStore.rootUser?.selectedOrgId) {
            localStorage.setItem('orgId', dataStore.rootUser.selectedOrgId);
          }
        }
      }

      if (!this.settingOrg()) {
        if (!dataStore.theming) {
          await saveOrgInfoLocal(dataStore.selectedOrganisation?.id ?? 'wemaron');
        }
      }

      if (dataStore.rootUser?.selectedOrgId) {
        await setPaidFunctionalities(dataStore.rootUser.selectedOrgId);
      }
      this.loaded.set(true);
    });
  }

  private isAuthAction(): boolean {
    return this.location.path().split('?')[0] === '/auth/actions';
  }

  async setOrganisation(orgId: string) {
    await updateDoc(doc(db, `users/${dataStore.rootUser.id}`), {
      selectedOrgId: orgId,
    });
    //TODO remove when dataStore is implemented everywhere in the system
    localStorage.setItem('orgId', orgId);

    location.reload();
  }

  async logout() {
    dataStore.subscribes.forEach((subscriber) => subscriber.unsub());
    await auth.signOut();
    // TODO remove after localstorage is removed in planning
    for (const [functionality, value] of Object.entries(this.dataStore.paidFunctionalities)) {
      localStorage.removeItem(functionality);
    }
    // end TODO
    return this.router.navigate(['/login']);
  }

  updateWidth(type: 'org' | 'profile', element: HTMLElement) {
    const width = `${element.clientWidth}px`;
    setTimeout(() => {
      if (type === 'org') {
        this.orgWidth.set(width);
      } else {
        this.profileWidth.set(width);
      }
    });
  }

  openHelpDialog() {
    this.showHelpPane.set(!this.showHelpPane());
  }
}

export async function setPaidFunctionalities(orgId: string) {
  try {
    const org = (await getDoc(doc(db, `organisations/${orgId}`))).data() as Organisation;
    const paidFunctionalities = org?.paidFunctionalities || {};
    for (const [functionality, value] of Object.entries(paidFunctionalities)) {
      localStorage.setItem(functionality, `${value}`);
    }
    updateDataStore('paidFunctionalities', paidFunctionalities);
  } catch (error) {
    updateDataStore('paidFunctionalities', {});
  }
}

export async function initDataStoreRootUser(
  user: User,
  iconRegistry: MatIconRegistry,
  sanitizer: DomSanitizer,
) {
  dataStore.rootUser = {
    ...(await getDoc(doc(db, `users/${user.uid}`))).data(),
    id: user.uid,
  } as RootUser;
  dataStore.rootUser.organisations = dataStore.rootUser.organisations.sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  dataStore.selectedOrganisation =
    dataStore.rootUser.organisations[
      dataStore.rootUser.organisations.findIndex(
        (org) => org.id === dataStore.rootUser.selectedOrgId,
      )
    ];
  updateDataStore('rootUser', dataStore.rootUser);
  updateDataStore('selectedOrganisation', dataStore.selectedOrganisation);

  if (!dataStore.selectedOrganisation) {
    return;
  }

  if (!dataStore.selectedOrganisation.roles || dataStore.selectedOrganisation.roles.length === 0) {
    dataStore.subscribes.forEach((subscriber) => subscriber.unsub());
    await auth.signOut();
  }
  if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'rootUser') === -1) {
    const unsubRootUser = onSnapshot(doc(db, `users/${user.uid}`), async (userDoc) => {
      dataStore.rootUser = {
        ...userDoc.data(),
        id: user.uid,
      } as RootUser;
      dataStore.rootUser.organisations = dataStore.rootUser.organisations.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      updateDataStore('rootUser', dataStore.rootUser);
    });
    dataStore.subscribes.push({ id: 'rootUser', unsub: unsubRootUser });
  }
  dataStore.selectedOrganisation.particularities = [];
  const particularitiesQuery = query(
    collection(db, `organisations/${dataStore.selectedOrganisation.id}/particularities`),
  );
  (await getDocs(particularitiesQuery)).forEach((particularityDoc) => {
    const particularity = { ...particularityDoc.data(), id: particularityDoc.id } as Particularity;
    if (
      particularity.active !== false &&
      !dataStore.selectedOrganisation.particularities.find((p) => p.id === particularity.id)
    ) {
      dataStore.selectedOrganisation.particularities.push(particularity);
    }
  });
  updateDataStore('selectedOrganisation', dataStore.selectedOrganisation);

  if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'particularities') === -1) {
    const unsubParticularities = onSnapshot(
      query(collection(db, `organisations/${dataStore.selectedOrganisation.id}/particularities`)),
      (snapShot) => {
        const particularities = [];
        snapShot.forEach((particularityDoc) => {
          const particularity = particularityDoc.data() as Particularity;
          if (particularity.active !== false) {
            particularities.push({
              ...particularity,
              id: particularityDoc.id,
            } as Particularity);
          }
          const options = {} as IconOptions;
          if (particularity.viewBox) {
            options['viewBox'] =
              `0 0 ${particularityDoc.data().viewBox.width} ${particularityDoc.data().viewBox.height}`;
          }
          // Remove existing icon if it exists to allow updating
          (iconRegistry as any)._svgIconConfigs.delete(particularityDoc.id);
          iconRegistry.addSvgIcon(
            particularityDoc.id,
            sanitizer.bypassSecurityTrustResourceUrl(particularity.iconUrl),
            options,
          );
        });
        particularities.sort((a, b) => a.name.localeCompare(b.name));
        if (dataStore.selectedOrganisation) {
          dataStore.selectedOrganisation.particularities = particularities;
          updateDataStore('selectedOrganisation', dataStore.selectedOrganisation);
        }
      },
    );
    dataStore.subscribes.push({ id: 'particularities', unsub: unsubParticularities });
  }
  dataStore.isMemberOfMultipleOrgs =
    dataStore.rootUser.organisations &&
    dataStore.rootUser.organisations.filter((org) => org.roles && org.roles.length > 0).length > 1
      ? true
      : false;
  updateDataStore('isMemberOfMultipleOrgs', dataStore.isMemberOfMultipleOrgs);
}

export async function initDataStorePublicSettings() {
  const publicSettingsDoc = await getDoc(
    doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/settings/public`),
  );
  dataStore.publicSettings = {
    ...publicSettingsDoc.data(),
    id: publicSettingsDoc.id,
  } as PublicSettings;
  updateDataStore('publicSettings', dataStore.publicSettings);

  if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'publicSettings') === -1) {
    const unsubPublicSettings = onSnapshot(
      doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/settings/public`),
      async (settingsDoc) => {
        dataStore.publicSettings = {
          ...settingsDoc.data(),
          id: settingsDoc.id,
        } as PublicSettings;
        updateDataStore('publicSettings', dataStore.publicSettings);
        await saveOrgInfoLocal(dataStore.selectedOrganisation.id);
      },
    );
    dataStore.subscribes.push({
      id: 'publicSettings',
      unsub: unsubPublicSettings,
    });
  }
}

export async function initDataStoreGeneralSettings() {
  try {
    const generalSettingsDoc = await getDoc(
      doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/settings/general`),
    );
    dataStore.generalSettings = {
      ...generalSettingsDoc.data(),
    } as Setting;
    updateDataStore('generalSettings', dataStore.generalSettings);

    if (dataStore.subscribes.findIndex((unsub) => unsub.id === 'generalSettings') === -1) {
      const unsubGeneralSettings = onSnapshot(
        doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/settings/general`),
        async (settingsDoc) => {
          dataStore.generalSettings = {
            ...settingsDoc.data(),
          } as Setting;
          updateDataStore('generalSettings', dataStore.generalSettings);
        },
        () => {
          // Ignore permission-denied or snapshot errors for users without general settings read access
        },
      );
      dataStore.subscribes.push({
        id: 'generalSettings',
        unsub: unsubGeneralSettings,
      });
    }
  } catch (error) {
    dataStore.generalSettings = {} as Setting;
    updateDataStore('generalSettings', dataStore.generalSettings);
  }
}

export async function initDataStoreOrgUser() {
  dataStore.orgUser = {
    ...(
      await getDoc(
        doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/users/${dataStore.rootUser.id}`),
      )
    ).data(),
    id: dataStore.rootUser.id,
  } as OrgUser;
  if (!dataStore.orgUser.particularityIds) {
    dataStore.orgUser.particularityIds = [];
  }
  updateDataStore('orgUser', dataStore.orgUser);

  if (dataStore.subscribes.findIndex((unsub) => unsub?.id === 'orgUser') === -1) {
    const unsubOrgUser = onSnapshot(
      doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/users/${dataStore.rootUser.id}`),
      async (userDoc) => {
        dataStore.orgUser = {
          ...userDoc.data(),
          id: dataStore.rootUser.id,
        } as OrgUser;
        if (!dataStore.orgUser.particularityIds) {
          dataStore.orgUser.particularityIds = [];
        }
        updateDataStore('orgUser', dataStore.orgUser);
      },
    );
    dataStore.subscribes.push({
      id: 'orgUser',
      unsub: unsubOrgUser,
    });
  }
}
